<script setup lang="ts">
    import { computed, inject, onMounted, reactive, ref, toRaw } from 'vue';
    import axios from 'axios';
    import ConfirmationModal from '@/components/ConfirmationModal.vue';
    import OnboardingGroupeComponent from '../onboarding-groupe/components/OnboardingGroupeComponent.vue';
    
    const BACKEND_URL = inject('BACKEND_URL');
    const errorMessage = ref('');
    const errorMessageListing = ref('');
    const loading = ref(false);
    const loadingListing = ref(false);
    const itemsList = ref([] as any[]);
    const selectedItem = ref(null as any);
    const confirmationModalRef = ref<InstanceType<typeof ConfirmationModal> | null>(null);
    const selectedItems = ref([] as any[]);
    const areAllItemsSelected = computed(() => {
        return itemsList.value.length == selectedItems.value.length;
    });
    const queryParams = reactive({
        pageNumber: 1,
        sortDir: 'ASC',
        sortColumn: 'nom',
        sortOption: 'nom_asc',
        borneInf: '',
        borneSup: '',
        pays_id: '',
        search: '',
        trashed: 0
    });
    const pagination = reactive({
        total: 0,
        per_page: 10,
        last_page: 1
    });
    const SORT_OPTIONS = [
        { value: 'nom_asc', label: 'Nom (ASC)', sort_column: 'nom', sort_direction: 'ASC' },
        { value: 'nom_desc', label: 'Nom (DESC)', sort_column: 'nom', sort_direction: 'DESC' },
        { value: 'created_at_asc', label: 'Date (ASC)', sort_column: 'created_at', sort_direction: 'ASC' },
        { value: 'created_at_desc', label: 'Date (DESC)', sort_column: 'created_at', sort_direction: 'DESC' },
    ];
    const showAdvancedFilters = ref(false);
    const isOnboardingModalOpened = ref(false);

    onMounted(() => {
        doListItems();
    });

    async function doListItems() {
        try {
            loadingListing.value = true;
            errorMessage.value = '';
            const queryParamsRaw = toRaw(queryParams);
            const queryString = new URLSearchParams(Object.fromEntries(Object.entries(queryParamsRaw).map(([k,v]) => [k, String(v)]))).toString();
            const httpResponse = await axios.get(`/backoffice/collectivites/list?${queryString}`);
            itemsList.value = httpResponse.data.data;
            pagination.total = httpResponse.data.total;
            pagination.per_page = httpResponse.data.per_page;
            pagination.last_page = httpResponse.data.last_page;
        } catch (error: any) {
            errorMessageListing.value = error.message;
        } finally {
            loadingListing.value = false;
        }
    }

    function onPaginate(pageNumber: number) {
        queryParams.pageNumber = pageNumber;
        doListItems();
    }

    async function doDeleteItem(item: any) {
        const confirmation = await confirmationModalRef.value?.open("Collectivités", "Voulez-vous vraiment supprimer l'élément sélectionné ?");
        if (confirmation) {
            try {
                loading.value = true;
                errorMessage.value = '';
                await axios.post(`/backoffice/collectivites/destroy/${item.id}`, {});
                const index = itemsList.value.findIndex(i => i.id == item.id);
                if (index > -1) {
                    const itemsListCloned = itemsList.value;
                    itemsListCloned.splice(index, 1);
                    itemsList.value = itemsListCloned;
                }
            } catch(error: any) {
                errorMessage.value = error.message;
            } finally {
                loading.value = false;
            }
        }
    }

    function handleCheckAll(_event: any) {
        const checked = _event.target.checked;
        if (checked) {
            selectedItems.value = itemsList.value.map(i => i.id);
        } else {
            selectedItems.value = [];
        }
    }

    function toggleAdvancedFilters() {
        showAdvancedFilters.value = !showAdvancedFilters.value;
    }

    function doOpenOnboardingModal(item: any) {
        selectedItem.value = item;
        isOnboardingModalOpened.value = true;
    }

    function onOnboardingModalClosed() {
        selectedItem.value = null;
        isOnboardingModalOpened.value = false;
    }

    function onOnboardingCompleted() {
        selectedItem.value = null;
        isOnboardingModalOpened.value = false;
        doListItems();
    }

</script>

<template>
    <section class="container-full-app bg-grey-50">
        <div class="page-space-30"></div>

        <!--*** Widget *** -->
        <section class="s20">
            <div class="widget shadow-100 s20 pb-2"><!-- widget shadow-100 fill -->
                <!--Card-->
                <div class="container-full-2 s20 my-5">
                    <div class="s7"><h4 class="font-semibold mt-4">Collectivites <span class="text-sm text-grey-500">(recent)</span></h4></div>
                    <div class="s13 flex justify-content-right">
                        <router-link to="/app/collectivites/create">
                            <button id="btn-add" class="btn normal ml-4 justify-content-center btn-filtre">
                                <img src="/icons/icon-add.svg" class="btn-icon" alt="" />
                                <span>Créer</span>
                            </button>
                        </router-link>
                        <div class="form-field-floating default sm s4_mobile ml-3">
                            <select class="form-input-select" name="pays" id="pays">
                                <option value="">Trier</option>
                                <option value="" selected>Ordre Décroissant</option>
                                <option value="">Ordre Croissant</option>
                                <option value="">De A - Z</option>
                                <option value="">De Z - A</option>
                            </select>
                        </div>
                        <button @click="toggleAdvancedFilters" class="btn normal white2 s3_mobile ml-4 justify-content-center btn-filtre btn-filter">
                            <img src="/icons/icon-filter.svg" class="btn-icon" alt="" />
                            <span>Options</span>
                        </button>
                    </div>
                    <div class="clear-both"></div>
                    <!--Options-->
                    <div class="custom-filter-set-area" v-show="showAdvancedFilters">
                        <div class="grid-filter6 mt-4 mb-0">
                            <div class="form-field-floating reactive sm">
                                <input type="date" class="form-input" name="date1" id="date1" placeholder="">
                                <label for="date1">Date de création &lt;=</label>
                            </div>
                            <div class="form-field-floating reactive sm">
                                <input type="date" class="form-input" name="date2" id="date2" placeholder="">
                                <label for="date2">Date de création >=</label>
                            </div>
                            <div class="form-field-floating sm">
                                <select class="form-input-select" name="pays" id="pays">
                                    <option value="" selected>Tout les etats</option>
                                    <option value="">Actif</option>
                                    <option value="">Inactif</option>
                                </select>
                                <label for="pays">Etat <b class="color-danger">*</b></label>
                            </div>
                            <div class="form-field-floating sm">
                                <select class="form-input-select" name="pays" id="pays">
                                    <option value="" selected>Tout les etats</option>
                                    <option value="">Actif</option>
                                    <option value="">Inactif</option>
                                </select>
                                <label for="pays">Etat <b class="color-danger">*</b></label>
                            </div>
                            <button class="btn black normal justify-content-center mx-0 btn-filtre">
                                <img src="/icons/icon-excel.svg" class="btn-icon" alt="" />
                                <span>Exporter Excel</span>
                            </button>
                            <button class="btn black normal justify-content-center mx-0 btn-filtre">
                                <img src="/icons/icon-pdf.svg" class="btn-icon" alt="" />
                                <span>Exporter PDF</span>
                            </button>
                        </div>
                    </div>
                    <!---->
                </div>
            </div>

            <div>
                <!-- Error Message Display -->
                <div v-if="errorMessageListing">
                    <div class="clear-both h-30"></div>
                    <div style="display: flex; flex-direction: column; align-items: center; justify-content: center;">
                        <img src="/icons/icon-fail.svg" class="size-lg mb-3" alt="Erreur" />
                        <p class="text-center text-grey-700 font-semibold">{{ errorMessageListing }}</p>
                    </div>
                </div>

                <!-- Table Content -->
                <template v-if="!errorMessageListing">
                    <table class="table separator skin3 selection rounded"><!-- selection skin1 skin2 (bg1 bg2 bg3)  (skin3 rounded)  sm-->
                        <thead>
                            <tr>
                                <th>
                                    <label class="btn-circle" for="check_all">
                                        <input type="checkbox" id="check_all" :checked="areAllItemsSelected" @change="handleCheckAll" />
                                    </label>
                                </th>
                                <th></th>
                                <th><img src="/icons/icon-filter-up.svg" class="size-xxs mt--1" alt="" /> Information</th>
                                <th><img src="/icons/icon-filter-up.svg" class="size-xxs mt--1" alt="" /> Membres</th>
                                <th><img src="/icons/icon-filter-up.svg" class="size-xxs mt--1" alt="" /> Prélèvement</th>
                                <th class="flex-right">
                                    <button class="btn-ultra color2">
                                        <div class="btn-show">
                                            <img src="/icons/icon-actif.svg" class="btn-icon" alt="" />
                                            <span>Actif</span>
                                        </div>
                                        <div class="btn-loading">
                                            <div class="loader-dots"></div>
                                        </div>
                                    </button>
                                </th>
                            </tr>
                        </thead>
                        <tbody>
                            <template v-if="loading">
                                <tr>
                                    <td colspan="6" class="text-center py-5">
                                        <div style="display: flex; justify-content: center" v-if="loading">
                                            <div class="loader dark"></div>
                                        </div>
                                    </td>
                                </tr>
                            </template>
                            <template v-else-if="itemsList.length === 0">
                                <tr>
                                    <td colspan="6" class="text-center py-5">
                                        Aucune collectivité trouvée.
                                    </td>
                                </tr>
                            </template>
                            <template v-else>
                                <tr class="text-grey-700 over-effect" v-for="(item, index) in itemsList" :key="'item_' + index"><!-- over-effect active-->
                                    <td class="selection-item">
                                        <label class="btn-circle" :for="'item_check_' + index">
                                            <input type="checkbox" name="selectedItems[]" :id="'item_check_' + index" :value="item.id" v-model="selectedItems" />
                                        </label>
                                    </td>
                                    <td>
                                        <div class="flex">
                                            <div class="square-xxl rounded-15 ml-3 px-3 py-3" style="background-color: rgb(var(--color-black-rgb), 0.04);">
                                                <img :src="item.logo ? (BACKEND_URL + '/assets/collectivites/' + item.logo) : '/assets/logos/logo2.svg'" width="100%" />
                                            </div>
                                        </div>
                                    </td>
                                    <td style="width: 25%;">
                                        <p class="text-xl font-semibold mb-2">
                                            {{ item.nom }} <br class="mb-3"> 
                                            {{ item.rccm || 'N/A' }} &nbsp;
                                            <span class="tag-state notif stroke">Actif</span>
                                        </p>
                                    </td>
                                    <td class="text-2xl" style="width: 20%;">
                                        <span class="text-2xl color-success">{{ item.users_count }}</span>/{{ item.prospects_count }}
                                        <p class="mt-1">Membres</p>
                                    </td>
                                    <td class="font-semibold" style="width: 20%;">
                                        Janvier 2024<br class="mb-5" />
                                        <span class="tag-state danger stroke">Pas à jour</span>
                                    </td>
                                    <td>
                                        <div class="flex-right gap-4">
                                            <router-link :to="'/app/collectivites/form/' + item.id">
                                                <button class="btn tips">
                                                    <img src="/icons/icon-edit.svg" class="btn-icon" alt="" />
                                                    <span>Editer</span>
                                                </button>
                                            </router-link>
                                            <button class="btn-ultra color2 tips">
                                                <div class="btn-show">
                                                    <img src="/icons/icon-actif.svg" class="btn-icon" alt="" />
                                                    <span>Actif</span>
                                                </div>
                                                <div class="btn-loading">
                                                    <div class="loader-dots"></div>
                                                </div>
                                            </button>
                                            <router-link :to="'/app/collectivites/manage/' + item.id">
                                                <button class="btn white2 active">
                                                    <img src="/icons/icon-consulter.svg" class="btn-icon" alt="" />
                                                    <span>Gérer</span>
                                                </button>
                                            </router-link>
                                            <router-link :to="'/app/collectivites/' + item.id + '/details'">
                                                <button class="btn white2 active">
                                                    <img src="/icons/icon-consulter.svg" class="btn-icon" alt="" />
                                                    <span>Profil</span>
                                                </button>
                                            </router-link>
                                            <router-link :to="'/app/collectivites/' + item.id + '/cartes'">
                                                <button class="btn white2 active">
                                                    <img src="/icons/icon-consulter.svg" class="btn-icon" alt="" />
                                                    <span>Cartes</span>
                                                </button>
                                            </router-link>
                                            <router-link :to="'/app/finance/prelevements/' + item.id">
                                                <button class="btn white2 active">
                                                    <img src="/icons/icon-consulter.svg" class="btn-icon" alt="" />
                                                    <span>Prélèvements / Décaissements</span>
                                                </button>
                                            </router-link>
                                        </div>
                                    </td>
                                </tr>
                            </template>
                        </tbody>
                    </table>
                    <!--Pagination-->
                    <div style="display: flex; flex-direction: row; justify-content: center;">
                        <custom-pagination v-model="queryParams.pageNumber" :total="pagination.total" :per-page="pagination.per_page" :last_page="pagination.last_page" @update:modelValue="onPaginate" />
                    </div>
                </template>
                <!---->
            </div>
            <div class="clear-both h-30"></div>
            
        </section>
        
        <div class="clear-both h-30"></div>
        <confirmation-modal ref="confirmationModalRef"></confirmation-modal>
        <custom-modal :classesNames="'modal-lg'" v-model:show="isOnboardingModalOpened" @closed="onOnboardingModalClosed">
            <template #header>&nbsp;</template>
            <div v-if="selectedItem">
                <onboarding-groupe-component :is-widget-mode="false" :collectivite="selectedItem" @completed="onOnboardingCompleted" />
            </div>
            <template #footer>&nbsp;</template>
        </custom-modal>


    </section>
</template>
